import type { AgGridReact, CellValueChangedEvent } from '@uturn/ui-kit';

export const CONTAINER_NUMBER_FIELD = 'containerNumber';

export const normalizeContainerNumber = (value: unknown) =>
	typeof value === 'string' && value.trim() !== ''
		? value.trim().toUpperCase()
		: undefined;

/**
 * Collect the set of container numbers that appear in more than one row of the
 * grid. Used to flag in-form duplicates with a non-blocking warning, separate
 * from the backend "already in use" check that runs on submit.
 */
export const getDuplicateContainerNumbers = (
	api: AgGridReact['api'],
): Set<string> => {
	const occurrencesByNumber = new Map<string, number>();

	api.forEachNode((node) => {
		const containerNumber = normalizeContainerNumber(
			node.data?.[CONTAINER_NUMBER_FIELD]?.value,
		);
		if (containerNumber !== undefined) {
			occurrencesByNumber.set(
				containerNumber,
				(occurrencesByNumber.get(containerNumber) ?? 0) + 1,
			);
		}
	});

	return new Set(
		[...occurrencesByNumber.entries()]
			.filter(([, occurrences]) => occurrences > 1)
			.map(([containerNumber]) => containerNumber),
	);
};

/**
 * Live validation: flag container numbers that are duplicated across rows
 * within the form with a non-blocking orange warning. This is independent
 * of the backend "already in use" check on submit, and triggers on any
 * container-number change (typing, paste or fill).
 */
export const validateContainerNumberDuplicatesOnCellChange = (
	event: CellValueChangedEvent,
	warningMessage: string,
) => {
	if (event.column.getColId() !== CONTAINER_NUMBER_FIELD) {
		return;
	}

	const duplicateContainerNumbers = getDuplicateContainerNumbers(event.api);

	const updatedRows = [];
	event.api.forEachNode((node) => {
		const cell = node.data?.[CONTAINER_NUMBER_FIELD];
		if (!cell) {
			return;
		}

		const containerNumber = normalizeContainerNumber(cell.value);
		const isDuplicate =
			containerNumber !== undefined &&
			duplicateContainerNumbers.has(containerNumber);
		const hadWarning = cell.isWarning === true;

		if (isDuplicate && !hadWarning) {
			updatedRows.push({
				...node.data,
				[CONTAINER_NUMBER_FIELD]: {
					...cell,
					isWarning: true,
					warningMessage,
				},
			});
		} else if (!isDuplicate && hadWarning) {
			updatedRows.push({
				...node.data,
				[CONTAINER_NUMBER_FIELD]: {
					...cell,
					isWarning: undefined,
					warningMessage: undefined,
				},
			});
		}
	});

	if (updatedRows.length > 0) {
		event.api.applyTransaction({ update: updatedRows });
		event.api.redrawRows();
	}
};
